"use client"

import { useState, useEffect } from "react"
import { useAuth } from "../../../contexts/AuthContext"
import {
  DevicePhoneMobileIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserCircleIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  PhoneIcon,
  StarIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline"
import { API_BASE_URL } from "../../../config/api"
import Swal from "sweetalert2"

// Format INR currency with two decimal places
const formatCurrency = (value) => {
  const num = typeof value === "number" ? value : Number(value) || 0
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num)
}

const Useraccount = () => {
  const {
    user,
    getAllUsers,
    createUser,
    editUser,
    deleteUser,
    impersonateUser,
    syncUserLimit,
    verifyEmail,
    sendVerificationEmail,
    canImpersonate,
    getUserPricing,
    setUserPricing,
    updateUserPricing,
    canManagePricing,
    getUserCountryPricing,
    updateResellerBranding,
    uploadResellerLogo,
  } = useAuth()

  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formError, setFormError] = useState("")

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserDetails, setShowUserDetails] = useState(false)
  const [showWabaModal, setShowWabaModal] = useState(false)
  const [selectedWabaUser, setSelectedWabaUser] = useState(null)

  // Reseller Branding Modal state
  const [showResellerBrandingModal, setShowResellerBrandingModal] = useState(false)
  const [selectedResellerForBranding, setSelectedResellerForBranding] = useState(null)
  const [resellerBrandingForm, setResellerBrandingForm] = useState({
    domain: "",
    ip: "",
    companyName: "",
    logo: "",
  })
  const [brandingLoading, setBrandingLoading] = useState(false)
  const [wabaModalForm, setWabaModalForm] = useState({
    label: "",
    phoneNumberId: "",
    cloudNumber: "",
    wabaId: "",
    token: "",
    editType: null,
    editIndex: null,
  })
  const [verificationCode, setVerificationCode] = useState("")
  const [verificationEmail, setVerificationEmail] = useState("")
  const [showPricingModal, setShowPricingModal] = useState(false)
  const [selectedUserForPricing, setSelectedUserForPricing] = useState(null)
  const [userCountryList, setUserCountryList] = useState([])
  const [loadingCountryList, setLoadingCountryList] = useState(false)
  const [myCountryRates, setMyCountryRates] = useState([])
  const [loadingMyRates, setLoadingMyRates] = useState(false)

  const isAgentManager = user?.role === "user"

  useEffect(() => {
    if (isAgentManager && (user?._id || user?.id)) {
      const fetchMyPricing = async () => {
        setLoadingMyRates(true)
        const res = await getUserCountryPricing(user._id || user.id)
        setLoadingMyRates(false)
        if (res.success && res.countries) {
          setMyCountryRates(res.countries)
        }
      }
      fetchMyPricing()
    }
  }, [isAgentManager, user?._id, user?.id])
  const [pricingData, setPricingData] = useState({
    marketing: "0.8",
    utility: "0.15",
    authentication: "0.15",
  })

  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    // Create user fields (simplified)
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    number: "",
    operator: "cloud",
    role: "user",
    requireEmailVerification: false,

    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    city: "",
    state: "",
    country: "",
    zipCode: "",
    jobTitle: "",
    company: "",
    department: "",
    bio: "",
    website: "",
    alternateEmail: "",
    alternatePhone: "",
    socialMedia: {
      facebook: "",
      twitter: "",
      linkedin: "",
      instagram: "",
      youtube: "",
      github: "",
    },
    profile: "",
    userStatus: "active",
    emailNotifications: true,
    smsNotifications: true,
    wptoken: "",
    template: "",
    wpCloudApi: "",
    cloudNumber: "",
    phoneNumberId: "",
    wabaId: "",
    businessId: "",
    businessAccountId: "",
    apiKey: "",
    token: "",
    appId: "",
    webhookUrl: "",
    balance: 0,
    expiry: "",
  })

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState("")
  const [roleFilter, setRoleFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [usersPerPage] = useState(10)

  // Role hierarchy for permissions
  const roleHierarchy = {
    superadmin: 5,
    admin: 4,
    reseller: 3,
    company: 2,
    user: 1,
    agent: 0,
  }

  const managedEntityLabel = isAgentManager ? "Agent" : "User"
  const listEntityLabel = isAgentManager ? "Agent" : "User"
  const listEntityLabelPlural = isAgentManager ? "agents" : "users"

  const canManageUser = (targetUser) => {
    if (!user || !targetUser) return false

    if (user.role === "user") {
      return user._id === targetUser._id || (targetUser.role === "agent" && (targetUser.createdBy?._id || targetUser.createdBy) === user._id)
    }
    if (user.role === "agent") return user._id === targetUser._id

    if (user.role === "superadmin") return true
    if (user.role === "admin" && ["admin", "reseller", "company", "user", "agent"].includes(targetUser.role)) return true
    if (user.role === "reseller") {
      if (!["company", "user"].includes(targetUser.role)) return false
      const creatorId = targetUser.createdBy?._id || targetUser.createdBy
      const parentIds = (targetUser.parentUsers || []).map((p) => (p?._id || p))
      return creatorId === user._id || parentIds.includes(user._id)
    }
    if (user.role === "company") {
      const creatorId = targetUser.createdBy?._id || targetUser.createdBy
      return targetUser.role === "user" && creatorId === user._id
    }
    return false
  }

  const canDeleteUser = (targetUser) => {
    if (!user || !targetUser) return false

    // Superadmin and Admin can delete anyone they manage
    if (["superadmin", "admin"].includes(user.role)) {
      return canManageUser(targetUser)
    }

    // Resellers can delete companies and users they manage
    if (user.role === "reseller") {
      return ["company", "user"].includes(targetUser.role) && canManageUser(targetUser)
    }

    // Companies can delete users they manage
    if (user.role === "company") {
      return targetUser.role === "user" && canManageUser(targetUser)
    }

    // Users can delete agents they manage
    if (user.role === "user") {
      return targetUser.role === "agent" && canManageUser(targetUser)
    }

    return false
  }

  const canCreateUser = () => {
    if (!user) return false
    return ["superadmin", "admin", "reseller", "user"].includes(user.role)
  }

  const canEditUser = (targetUser) => {
    if (!user || !targetUser) return false

    if (user.role === "user") {
      return targetUser.role === "agent" && (targetUser.createdBy?._id || targetUser.createdBy) === user._id
    }
    if (user.role === "agent") return false

    return ["superadmin", "admin", "reseller"].includes(user.role) && canManageUser(targetUser)
  }

  const canImpersonateUser = (targetUser) => {
    if (!user || !targetUser) return false

    // Users cannot impersonate
    if (["user", "agent"].includes(user.role)) {
      return false
    }

    return ["superadmin", "admin", "reseller"].includes(user.role) && canManageUser(targetUser)
  }

  const canCreateRole = (role) => {
    if (!user) return false
    if (role === "agent") return user.role === "user"
    if (user.role === "user") return role === "agent"
    if (user.role === "agent") return false
    if (user.role === "superadmin") return ["superadmin", "admin", "reseller", "company", "user"].includes(role)
    if (user.role === "admin") return ["reseller", "company", "user"].includes(role)
    if (user.role === "reseller") return role === "user"
    if (user.role === "company") return role === "user"
    return false
  }

  const getVisibleUsers = () => {
    if (!user) return []

    if (user.role === "user") {
      return users.filter((u) => u.role === "agent" && (u.createdBy?._id || u.createdBy) === user._id)
    }
    if (user.role === "agent") return users.filter((u) => u._id === user._id)

    // Other roles see users they can manage or themselves
    return users.filter((u) => u._id === user._id || canManageUser(u))
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const result = await getAllUsers()

      if (result.success) {
        let fetchedUsers = result.users

        // If normal user login, make sure their own account is included
        if (user?.role === "user") {
          const selfUser = fetchedUsers.find(u => u._id === user._id)
          if (!selfUser) {
            fetchedUsers = [
              ...fetchedUsers,
              {
                ...user,
                balance: user.balance ?? 0,
                operator: user.operator ?? "cloud",
                userStatus: user.userStatus ?? "active",
                createdAt: user.createdAt ?? new Date().toISOString(),
              }
            ]
          }
        }


        setUsers(fetchedUsers)
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError("Failed to fetch users")
    } finally {
      setLoading(false)
    }
  }

  const openResellerBrandingModal = (u) => {
    setSelectedResellerForBranding(u)
    setResellerBrandingForm({
      domain: u.resellerBranding?.domain || "",
      ip: u.resellerBranding?.ip || "",
      companyName: u.resellerBranding?.companyName || "",
      logo: u.resellerBranding?.logo || "",
    })
    setShowResellerBrandingModal(true)
  }

  const handleSaveResellerBranding = async (e) => {
    e.preventDefault()
    if (!selectedResellerForBranding) return
    try {
      setBrandingLoading(true)
      const res = await updateResellerBranding({
        userId: selectedResellerForBranding._id,
        domain: resellerBrandingForm.domain,
        ip: resellerBrandingForm.ip,
        companyName: resellerBrandingForm.companyName,
        logo: resellerBrandingForm.logo,
      })
      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Branding Updated",
          text: "Reseller white-label domain & branding updated successfully!",
          confirmButtonColor: "#4A7C59",
        })
        setShowResellerBrandingModal(false)
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: res.error || "Failed to update branding",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "An unexpected error occurred",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setBrandingLoading(false)
    }
  }

  const handleLogoUploadInModal = async (file) => {
    if (!file || !selectedResellerForBranding) return
    try {
      setBrandingLoading(true)
      const res = await uploadResellerLogo(file, selectedResellerForBranding._id)
      if (res.success && res.logo) {
        setResellerBrandingForm((prev) => ({
          ...prev,
          logo: res.logo,
        }))
        Swal.fire({
          icon: "success",
          title: "Logo Uploaded",
          text: "Reseller logo image uploaded successfully!",
          confirmButtonColor: "#4A7C59",
        })
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: res.error || "Failed to upload logo",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Failed to upload logo",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setBrandingLoading(false)
    }
  }


  const nextStep = () => {
    if (currentStep === 4 && !["user", "agent"].includes(formData.role)) {
      setCurrentStep(6)
    } else if (currentStep < 6) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep === 6 && !["user", "agent"].includes(formData.role)) {
      setCurrentStep(4)
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const navigateToMetaPricing = () => {
    window.location.href = "/meta"
  }

  const navigateToAddBalance = () => {
    window.location.href = "/recharge"
  }

  const getPasswordError = (pwd) => {
    if (!pwd) return null
    if (pwd.length < 8) {
      return "Password must be at least 8 characters long"
    }
    const hasUppercase = /[A-Z]/.test(pwd)
    const hasLowercase = /[a-z]/.test(pwd)
    const hasNumber = /[0-9]/.test(pwd)
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)
    if (!(hasUppercase && hasLowercase && hasNumber && hasSpecial)) {
      return "Password must contain capital (uppercase), lowercase, numeric, and special characters"
    }
    return null
  }

  const getConfirmPasswordError = (confirmPwd, pwd) => {
    if (!confirmPwd) return null
    if (pwd !== confirmPwd) {
      return "Passwords do not match"
    }
    return null
  }

  const validatePasswordStrength = (pwd) => {
    return getPasswordError(pwd) === null
  }

  const handleCreateUser = async (e) => {
    e.preventDefault()
    setSubmitted(true)
    setFormError("")

    // Manual validation for required fields
    const requiredFields = ["username", "email", "password", "confirmPassword", "number"]
    const hasBlankFields = requiredFields.some((f) => !formData[f] || !formData[f].trim())
    if (hasBlankFields) {
      setFormError("Please fill out all required fields.")
      return
    }

    // Password strength check
    if (!validatePasswordStrength(formData.password)) {
      setFormError("Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.")
      return
    }

    // Passwords match check
    if (formData.password !== formData.confirmPassword) {
      setFormError("Passwords do not match.")
      return
    }

    const result = await Swal.fire({
      title: `Create ${managedEntityLabel}?`,
      text: `Are you sure you want to create ${managedEntityLabel.toLowerCase()} ${formData.username || formData.email}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4A7C59",
      cancelButtonColor: "#6B7280",
      confirmButtonText: `Yes, create ${managedEntityLabel.toLowerCase()}`,
    })

    if (!result.isConfirmed) return

    setLoading(true)
    setError("")

    try {
      // Create user first with pending status if verification is required
      const userDataToCreate = {
        ...formData,
        role: user?.role === "user" ? "agent" : formData.role,
        operator: user?.role === "user" ? user?.operator || formData.operator : formData.operator,
        bypassEmailVerification: !formData.requireEmailVerification,
      }

      const createResult = await createUser(userDataToCreate)

      if (createResult.success) {
        // If email verification is required, send verification email after user creation
        if (formData.requireEmailVerification) {
          try {
            const emailResult = await sendVerificationEmail(formData.email)
            if (emailResult.success) {
              setVerificationEmail(formData.email)
              setShowVerificationModal(true)
              setShowCreateModal(false)

              Swal.fire({
                icon: "success",
                title: `${managedEntityLabel} Created!`,
                text: `${managedEntityLabel} created successfully. Please check your email for the verification code.`,
                confirmButtonColor: "#4A7C59",
              })
            } else {
              setFormError(`${managedEntityLabel} created but failed to send verification email. You can resend it later.`)
            }
          } catch (err) {
            setFormError(`${managedEntityLabel} created but failed to send verification email. You can resend it later.`)
          }
        } else {
          // Direct creation without verification
          Swal.fire({
            icon: "success",
            title: "Success!",
            text: `${managedEntityLabel} created successfully!`,
            confirmButtonColor: "#4A7C59",
          })
          setShowCreateModal(false)
        }

        resetForm()
        fetchUsers()
      } else {
        setFormError(createResult.error || `Failed to create ${managedEntityLabel.toLowerCase()}`)
      }
    } catch (err) {
      setFormError(err.message || `Failed to create ${managedEntityLabel.toLowerCase()}`)
    } finally {
      setLoading(false)
    }
  }

  const createUserDirectly = async () => {
    try {
      setLoading(true)
      const createResult = await createUser({
        ...formData,
        role: user?.role === "user" ? "agent" : formData.role,
        operator: user?.role === "user" ? user?.operator || formData.operator : formData.operator,
        isEmailVerified: true, // Mark as verified since it's already verified
        bypassEmailVerification: true, // Bypass verification process
      })

      if (createResult.success) {
        Swal.fire({
          icon: "success",
          title: `${managedEntityLabel} Created!`,
          text: `${managedEntityLabel} created and email verified successfully!`,
          confirmButtonColor: "#4A7C59",
        })
        setShowCreateModal(false)
        resetForm()
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: createResult.error,
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `Failed to create ${managedEntityLabel.toLowerCase()} after verification`,
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSendVerificationCode = async (targetUser) => {
    try {
      setLoading(true)
      const result = await sendVerificationEmail(targetUser.email)

      if (result.success) {
        setVerificationEmail(targetUser.email)
        setShowVerificationModal(true)
        Swal.fire({
          icon: "success",
          title: "Verification Code Sent",
          text: `Verification code sent to ${targetUser.email}`,
          confirmButtonColor: "#4A7C59",
        })
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to send verification code",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to send verification code",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyEmail = async () => {
    if (!verificationCode) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please enter the verification code",
        confirmButtonColor: "#4A7C59",
      })
      return
    }

    try {
      setLoading(true)
      const verifyResult = await verifyEmail(verificationEmail, verificationCode)

      if (verifyResult.success) {
        if (showCreateModal || formData.username) {
          // Creating new user after verification
          await createUserDirectly()
        } else {
          // Just verifying existing user
          Swal.fire({
            icon: "success",
            title: "Email Verified!",
            text: "Email has been verified successfully",
            confirmButtonColor: "#4A7C59",
          })
          fetchUsers()
        }
        setShowVerificationModal(false)
        setVerificationCode("")
        setVerificationEmail("")
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: verifyResult.error || "Invalid verification code",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to verify email",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const toggleUserStatus = async (targetUser) => {
    const newStatus = targetUser.userStatus === "active" ? "inactive" : "active"

    const result = await Swal.fire({
      title: `${newStatus === "active" ? "Activate" : "Deactivate"} User?`,
      text: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} ${targetUser.username || targetUser.email}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4A7C59",
      cancelButtonColor: "#6B7280",
      confirmButtonText: `Yes, ${newStatus === "active" ? "activate" : "deactivate"}`,
    })

    if (!result.isConfirmed) return

    try {
      setLoading(true)
      const updateResult = await editUser(targetUser._id, { userStatus: newStatus })

      if (updateResult.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `User ${newStatus === "active" ? "activated" : "deactivated"} successfully!`,
          confirmButtonColor: "#4A7C59",
        })
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: updateResult.error,
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update user status",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyUser = async (targetUser) => {
    const result = await Swal.fire({
      title: "Verify User Email?",
      text: `Mark ${targetUser.username || targetUser.email}'s email as verified?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4A7C59",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, verify email",
    })

    if (!result.isConfirmed) return

    try {
      setLoading(true)
      const updateResult = await editUser(targetUser._id, { isEmailVerified: true })

      if (updateResult.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "User email verified successfully!",
          confirmButtonColor: "#4A7C59",
        })
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: updateResult.error,
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to verify user email",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateUser = async (e) => {
    e.preventDefault()
    setSubmitted(true)
    setFormError("")

    // Manual validation for required fields
    if (!formData.username || !formData.username.trim() || !formData.email || !formData.email.trim()) {
      setFormError("Please fill out all required fields.")
      return
    }

    if (isAgentManager && (!formData.number || !formData.number.trim())) {
      setFormError("Phone number is required.")
      return
    }

    // Password strength check (only if password is being changed)
    if (formData.password && formData.password.trim() !== "") {
      if (!validatePasswordStrength(formData.password)) {
        setFormError("Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.")
        return
      }
    }

    const result = await Swal.fire({
      title: "Update User?",
      text: `Are you sure you want to update ${selectedUser.username || selectedUser.email}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4A7C59",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, update user",
    })

    if (!result.isConfirmed) return

    setLoading(true)
    setError("")

    try {
      const updateResult = await editUser(selectedUser._id, formData)

      if (updateResult.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "User updated successfully!",
          confirmButtonColor: "#4A7C59",
        })
        setShowEditModal(false)
        resetForm()
        fetchUsers()
      } else {
        setFormError(updateResult.error || "Failed to update user")
      }
    } catch (err) {
      setFormError(err.message || "Failed to update user")
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (targetUser) => {
    const result = await Swal.fire({
      title: "Delete User?",
      text: `Are you sure you want to delete ${targetUser.username || targetUser.email}? This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, delete user",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) return

    setLoading(true)

    try {
      const deleteResult = await deleteUser(targetUser._id)

      if (deleteResult.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "User has been deleted successfully.",
          confirmButtonColor: "#4A7C59",
        })
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: deleteResult.error,
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete user",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImpersonate = async (targetUser) => {
    const result = await Swal.fire({
      title: "Impersonate User?",
      text: `Are you sure you want to impersonate ${targetUser.username || targetUser.email}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#8B5CF6",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, impersonate",
    })

    if (!result.isConfirmed) return

    try {
      const impersonateResult = await impersonateUser(targetUser._id)

      if (impersonateResult.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Now impersonating ${targetUser.username || targetUser.email}`,
          confirmButtonColor: "#4A7C59",
        }).then(() => {
          window.location.reload()
        })
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: impersonateResult.error,
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to impersonate user",
        confirmButtonColor: "#4A7C59",
      })
    }
  }

  const handleViewUser = async (targetUser) => {
    // Prepare display values with safe fallbacks
    const name = targetUser.firstName || targetUser.username || targetUser.company || targetUser.name || "N/A"
    const email = targetUser.email || "N/A"
    const role = targetUser.role || targetUser.userRole || "N/A"
    const status = targetUser.userStatus || targetUser.status || "N/A"
    const phone = targetUser.number || targetUser.phone || "N/A"
    const balance = typeof targetUser.balance === "number" ? targetUser.balance : Number(targetUser.balance) || 0
    const emailVerified = targetUser.isEmailVerified === true || targetUser.emailVerified === true

    // Format date safely
    let createdAt = "N/A"
    if (targetUser.createdAt) {
      try {
        const d = new Date(targetUser.createdAt)
        if (!isNaN(d)) createdAt = d.toLocaleDateString()
      } catch (e) {
        createdAt = "N/A"
      }
    }

    // Format balance in INR with two decimals
    const formattedBalance = formatCurrency(balance)

    // Open right sidebar with selected user details
    setSelectedUser({
      displayName: name,
      email,
      role,
      status,
      phone,
      balance: formattedBalance,
      emailVerified,
      createdAt,
      raw: targetUser,
    })
    setShowUserDetails(true)
  }

  const handleViewWaba = (targetUser) => {
    setSelectedWabaUser(targetUser)
    setWabaModalForm({
      label: "",
      phoneNumberId: "",
      cloudNumber: "",
      token: "",
      editType: null,
      editIndex: null,
    })
    setShowWabaModal(true)
  }

  const maskToken = (token) => {
    if (!token) return "Not Configured"
    const str = String(token)
    if (str.length <= 5) return str + "****"
    return str.slice(0, 5) + "****"
  }

  const handleStartDirectWabaEdit = async (type, idx, item) => {
    const result = await Swal.fire({
      title: "Edit Number?",
      text: "Are you sure you want to edit this WhatsApp sender number?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10B981",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, edit"
    })
    if (result.isConfirmed) {
      if (type === "primary") {
        setWabaModalForm({
          label: item.label || item.username || item.email || "",
          phoneNumberId: item.phoneNumberId || "",
          cloudNumber: item.cloudNumber || item.number || "",
          wabaId: item.wabaId || item.businessAccountId || item.businessId || "",
          token: item.token || item.apiKey || item.wptoken || "",
          editType: "primary",
          editIndex: 0,
        })
      } else {
        setWabaModalForm({
          label: item.label || "",
          phoneNumberId: item.phoneNumberId || "",
          cloudNumber: item.cloudNumber || "",
          wabaId: item.wabaId || item.businessAccountId || item.businessId || "",
          token: item.token || "",
          editType: "additional",
          editIndex: idx,
        })
      }
    }
  }


  const handleEditLimit = async (userToEdit, wabaIndex = null) => {
    let currentLimit = "";
    let numberDisplay = "";

    if (wabaIndex !== null && userToEdit.wabaNumbers && userToEdit.wabaNumbers[wabaIndex]) {
      currentLimit = userToEdit.wabaNumbers[wabaIndex].wabaLimit || "";
      numberDisplay = userToEdit.wabaNumbers[wabaIndex].cloudNumber || userToEdit.wabaNumbers[wabaIndex].label || `Additional Number ${wabaIndex + 1}`;
    } else {
      currentLimit = userToEdit.wabaLimit || "";
      numberDisplay = userToEdit.cloudNumber || userToEdit.label || "Primary Number";
    }

    const { value: limitVal } = await Swal.fire({
      title: 'Edit WABA Limit',
      html: `<p class="mb-2 text-sm text-gray-600 dark:text-gray-400">Editing limit for: <span class="font-semibold text-gray-900 dark:text-white">${numberDisplay}</span></p>`,
      input: 'select',
      inputOptions: {
        '250': '250',
        '1K': '1K',
        '2K': '2K',
        '10K': '10K',
        '100k': '100k',
        'Unlimited': 'Unlimited'
      },
      inputPlaceholder: 'Select limit tier',
      inputValue: currentLimit,
      showCancelButton: true,
      confirmButtonColor: "#4A7C59",
      inputValidator: (value) => {
        if (!value) {
          return 'You need to select a limit!'
        }
      }
    })


    if (limitVal !== undefined && limitVal !== currentLimit) {
      try {
        setLoading(true)
        let updatedFields = {};
        if (wabaIndex !== null && userToEdit.wabaNumbers) {
          const updatedWabaNumbers = [...userToEdit.wabaNumbers];
          if (updatedWabaNumbers[wabaIndex]) {
            updatedWabaNumbers[wabaIndex] = {
              ...updatedWabaNumbers[wabaIndex],
              wabaLimit: limitVal,
              limitUpdatedAt: new Date().toISOString()
            };
          }
          updatedFields = { wabaNumbers: updatedWabaNumbers };
        } else {
          updatedFields = {
            wabaLimit: limitVal,
            limitUpdatedAt: new Date().toISOString()
          };
        }

        const res = await editUser(userToEdit._id, updatedFields)
        if (res && res.success) {
          setUsers(users.map(u => u._id === userToEdit._id ? { ...u, ...updatedFields } : u))
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Limit updated successfully',
            confirmButtonColor: "#4A7C59",
          })
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: res?.error || res?.message || 'Failed to update limit',
            confirmButtonColor: "#4A7C59",
          })
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'An error occurred',
          confirmButtonColor: "#4A7C59",
        })
      } finally {
        setLoading(false)
      }
    }
  }

  const handleSyncLimit = async (userToSync, wabaIndex = null) => {
    try {
      setLoading(true)
      const res = await syncUserLimit(userToSync._id, wabaIndex)
      if (res && res.success) {
        setUsers(users.map(u => {
          if (u._id === userToSync._id) {
            if (wabaIndex !== null && u.wabaNumbers) {
              const updatedWabaNumbers = [...u.wabaNumbers];
              if (updatedWabaNumbers[wabaIndex]) {
                updatedWabaNumbers[wabaIndex] = {
                  ...updatedWabaNumbers[wabaIndex],
                  wabaLimit: res.wabaLimit,
                  limitUpdatedAt: res.limitUpdatedAt
                };
              }
              return { ...u, wabaNumbers: updatedWabaNumbers };
            } else {
              return { ...u, wabaLimit: res.wabaLimit, limitUpdatedAt: res.limitUpdatedAt };
            }
          }
          return u;
        }))
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Limit synced successfully from Meta',
          confirmButtonColor: "#4A7C59",
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: res?.error || res?.message || 'Failed to sync limit',
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred during sync',
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancelWabaEdit = () => {
    setWabaModalForm({
      label: "",
      phoneNumberId: "",
      cloudNumber: "",
      wabaId: "",
      token: "",
      editType: null,
      editIndex: null,
    })
  }

  const handleSaveDirectWabaEdit = async () => {
    if (!selectedWabaUser) return
    if (!wabaModalForm.phoneNumberId && !wabaModalForm.cloudNumber && !wabaModalForm.token) {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please enter at least Phone Number ID, Cloud Number, or Token.",
        confirmButtonColor: "#4A7C59",
      })
      return
    }

    // Ensure Label, Phone Number ID, WabaId, Cloud Number, and Token are not duplicated across sender numbers
    const newLabel = (wabaModalForm.label || "").trim().toLowerCase()
    const newPhoneId = (wabaModalForm.phoneNumberId || "").trim().toLowerCase()
    const newCloudNum = (wabaModalForm.cloudNumber || "").trim().toLowerCase()
    const newWabaId = (wabaModalForm.wabaId || "").trim().toLowerCase()
    const newToken = (wabaModalForm.token || "").trim().toLowerCase()

    const checkDuplicateAgainst = (w, isPrimary) => {
      const lbl = (w.label || (isPrimary ? (w.username || "") : "") || "").trim().toLowerCase()
      const phoneId = (w.phoneNumberId || "").trim().toLowerCase()
      const cloudNum = (w.cloudNumber || (isPrimary ? (w.number || "") : "") || "").trim().toLowerCase()
      const waba = (w.wabaId || (isPrimary ? (w.businessAccountId || w.businessId || "") : "") || "").trim().toLowerCase()
      const tok = (w.token || (isPrimary ? (w.apiKey || w.wptoken || "") : "") || "").trim().toLowerCase()

      if (newLabel && lbl === newLabel) return { field: "Label", value: wabaModalForm.label }
      if (newPhoneId && phoneId === newPhoneId) return { field: "Phone Number ID", value: wabaModalForm.phoneNumberId }
      if (newCloudNum && cloudNum === newCloudNum) return { field: "Cloud Number", value: wabaModalForm.cloudNumber }
      if (newWabaId && waba === newWabaId) return { field: "WabaId", value: wabaModalForm.wabaId }
      if (newToken && tok === newToken) return { field: "Token", value: wabaModalForm.token }
      return null
    }

    const existingWabas = selectedWabaUser.wabaNumbers || []
    let duplicateError = null

    if (wabaModalForm.editType === "primary") {
      for (const w of existingWabas) {
        duplicateError = checkDuplicateAgainst(w, false)
        if (duplicateError) break
      }
    } else if (wabaModalForm.editType === "additional" && wabaModalForm.editIndex !== null) {
      duplicateError = checkDuplicateAgainst(selectedWabaUser, true)
      if (!duplicateError) {
        for (let idx = 0; idx < existingWabas.length; idx++) {
          if (idx !== wabaModalForm.editIndex) {
            duplicateError = checkDuplicateAgainst(existingWabas[idx], false)
            if (duplicateError) break
          }
        }
      }
    } else {
      duplicateError = checkDuplicateAgainst(selectedWabaUser, true)
      if (!duplicateError) {
        for (const w of existingWabas) {
          duplicateError = checkDuplicateAgainst(w, false)
          if (duplicateError) break
        }
      }
    }

    if (duplicateError) {
      Swal.fire({
        icon: "error",
        title: `Duplicate ${duplicateError.field} Found`,
        text: `The ${duplicateError.field} "${duplicateError.value}" is already used by another sender number in this account. Duplicate values are not allowed.`,
        confirmButtonColor: "#4A7C59"
      })
      return
    }

    setLoading(true)
    try {
      let updatedPayload = {}
      if (wabaModalForm.editType === "primary") {
        updatedPayload = {
          label: wabaModalForm.label,
          username: wabaModalForm.label || selectedWabaUser.username,
          phoneNumberId: wabaModalForm.phoneNumberId,
          cloudNumber: wabaModalForm.cloudNumber,
          number: wabaModalForm.cloudNumber,
          wabaId: wabaModalForm.wabaId || selectedWabaUser.wabaId,
          businessAccountId: wabaModalForm.wabaId || selectedWabaUser.businessAccountId,
          businessId: wabaModalForm.wabaId || selectedWabaUser.businessId,
          token: wabaModalForm.token,
          apiKey: wabaModalForm.token,
          wptoken: wabaModalForm.token,
        }
      } else if (wabaModalForm.editType === "additional" && wabaModalForm.editIndex !== null) {
        const newWabaList = [...(selectedWabaUser.wabaNumbers || [])]
        newWabaList[wabaModalForm.editIndex] = {
          ...newWabaList[wabaModalForm.editIndex],
          label: wabaModalForm.label,
          phoneNumberId: wabaModalForm.phoneNumberId,
          cloudNumber: wabaModalForm.cloudNumber,
          wabaId: wabaModalForm.wabaId || "",
          token: wabaModalForm.token,
        }
        updatedPayload = {
          wabaNumbers: newWabaList,
        }
      } else {
        const newItem = {
          label: wabaModalForm.label || `Sender #${(selectedWabaUser.wabaNumbers?.length || 0) + 1}`,
          phoneNumberId: wabaModalForm.phoneNumberId,
          cloudNumber: wabaModalForm.cloudNumber,
          wabaId: wabaModalForm.wabaId || "",
          token: wabaModalForm.token,
          isActive: true,
        }
        const newWabaList = [...(selectedWabaUser.wabaNumbers || []), newItem]
        updatedPayload = {
          wabaNumbers: newWabaList,
        }
      }

      const res = await editUser(selectedWabaUser._id, updatedPayload)
      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Saved!",
          text: "WhatsApp sender number saved successfully.",
          confirmButtonColor: "#4A7C59",
          timer: 1500,
          showConfirmButton: false,
        })

        if (wabaModalForm.editType === "primary") {
          setSelectedWabaUser({
            ...selectedWabaUser,
            label: wabaModalForm.label,
            username: wabaModalForm.label || selectedWabaUser.username,
            phoneNumberId: wabaModalForm.phoneNumberId,
            cloudNumber: wabaModalForm.cloudNumber,
            number: wabaModalForm.cloudNumber,
            wabaId: wabaModalForm.wabaId || selectedWabaUser.wabaId,
            businessAccountId: wabaModalForm.wabaId || selectedWabaUser.businessAccountId,
            businessId: wabaModalForm.wabaId || selectedWabaUser.businessId,
            token: wabaModalForm.token,
            apiKey: wabaModalForm.token,
            wptoken: wabaModalForm.token,
          })
        } else if (wabaModalForm.editType === "additional" && wabaModalForm.editIndex !== null) {
          const newWabaList = [...(selectedWabaUser.wabaNumbers || [])]
          newWabaList[wabaModalForm.editIndex] = {
            ...newWabaList[wabaModalForm.editIndex],
            label: wabaModalForm.label,
            phoneNumberId: wabaModalForm.phoneNumberId,
            cloudNumber: wabaModalForm.cloudNumber,
            wabaId: wabaModalForm.wabaId || "",
            token: wabaModalForm.token,
          }
          setSelectedWabaUser({
            ...selectedWabaUser,
            wabaNumbers: newWabaList,
          })
        } else {
          const newItem = {
            label: wabaModalForm.label || `Sender #${(selectedWabaUser.wabaNumbers?.length || 0) + 1}`,
            phoneNumberId: wabaModalForm.phoneNumberId,
            cloudNumber: wabaModalForm.cloudNumber,
            wabaId: wabaModalForm.wabaId || "",
            token: wabaModalForm.token,
            isActive: true,
          }
          setSelectedWabaUser({
            ...selectedWabaUser,
            wabaNumbers: [...(selectedWabaUser.wabaNumbers || []), newItem],
          })
        }

        setWabaModalForm({
          label: "",
          phoneNumberId: "",
          cloudNumber: "",
          token: "",
          editType: null,
          editIndex: null,
        })
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.error || "Failed to update sender number.",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Failed to update sender number.",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSetPrimaryWaba = async (idx) => {
    if (!selectedWabaUser || !selectedWabaUser.wabaNumbers || !selectedWabaUser.wabaNumbers[idx]) return
    const waba = selectedWabaUser.wabaNumbers[idx]

    const result = await Swal.fire({
      title: "Set as Primary?",
      text: `Are you sure you want to make "${waba.label || waba.cloudNumber || waba.phoneNumberId}" the Primary WhatsApp sender number?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10B981",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, Set Primary"
    })

    if (!result.isConfirmed) return

    setLoading(true)
    try {
      const oldPrimary = {
        phoneNumberId: selectedWabaUser.phoneNumberId || "",
        cloudNumber: selectedWabaUser.cloudNumber || selectedWabaUser.number || "",
        wabaId: selectedWabaUser.businessAccountId || selectedWabaUser.wabaId || "",
        token: selectedWabaUser.token || selectedWabaUser.apiKey || selectedWabaUser.wptoken || "",
        label: selectedWabaUser.label ? `${selectedWabaUser.label} (Previous Primary)` : (selectedWabaUser.username ? `${selectedWabaUser.username} (Previous Primary)` : "Previous Primary")
      }

      const newWabaList = [...selectedWabaUser.wabaNumbers]
      newWabaList[idx] = oldPrimary

      const newPrimaryToken = waba.token || oldPrimary.token

      const updatePayload = {
        label: waba.label || "Primary",
        phoneNumberId: waba.phoneNumberId || "",
        cloudNumber: waba.cloudNumber || "",
        number: waba.cloudNumber || "",
        wabaId: waba.wabaId || "",
        businessAccountId: waba.wabaId || "",
        token: newPrimaryToken,
        apiKey: newPrimaryToken,
        wptoken: newPrimaryToken,
        wabaNumbers: newWabaList
      }

      const res = await editUser(selectedWabaUser._id, updatePayload)
      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Primary sender number updated successfully.",
          confirmButtonColor: "#4A7C59"
        })
        setSelectedWabaUser({
          ...selectedWabaUser,
          label: waba.label || "Primary",
          phoneNumberId: waba.phoneNumberId || "",
          cloudNumber: waba.cloudNumber || "",
          number: waba.cloudNumber || "",
          wabaId: waba.wabaId || "",
          businessAccountId: waba.wabaId || "",
          token: newPrimaryToken,
          apiKey: newPrimaryToken,
          wptoken: newPrimaryToken,
          wabaNumbers: newWabaList
        })
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.error || "Failed to update primary sender number."
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Failed to update primary sender number."
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteWabaNumber = async (type, idx, waba) => {
    if (!selectedWabaUser) return

    const labelName = waba?.label || waba?.cloudNumber || waba?.phoneNumberId || (type === "primary" ? "Primary Sender" : `Sender #${idx + 1}`)

    const result = await Swal.fire({
      title: "Delete Sender Number?",
      text: `Are you sure you want to delete "${labelName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, Delete",
    })

    if (!result.isConfirmed) return

    setLoading(true)
    try {
      let updatePayload = {}
      if (type === "primary") {
        updatePayload = {
          phoneNumberId: "",
          cloudNumber: "",
          number: "",
          token: "",
          apiKey: "",
          wptoken: "",
        }
      } else {
        const newWabaList = (selectedWabaUser.wabaNumbers || []).filter((_, i) => i !== idx)
        updatePayload = {
          wabaNumbers: newWabaList,
        }
      }

      const res = await editUser(selectedWabaUser._id, updatePayload)
      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "WhatsApp sender number deleted successfully.",
          confirmButtonColor: "#4A7C59",
          timer: 1500,
          showConfirmButton: false,
        })
        if (type === "primary") {
          setSelectedWabaUser({
            ...selectedWabaUser,
            ...updatePayload,
          })
        } else {
          setSelectedWabaUser({
            ...selectedWabaUser,
            wabaNumbers: updatePayload.wabaNumbers,
          })
        }
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.error || "Failed to delete sender number.",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Failed to delete sender number.",
      })
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      number: "",
      operator: isAgentManager ? user?.operator || "cloud" : "cloud",
      role: isAgentManager ? "agent" : "user",
      requireEmailVerification: false,
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      gender: "",
      address: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      jobTitle: "",
      company: "",
      department: "",
      bio: "",
      website: "",
      alternateEmail: "",
      alternatePhone: "",
      socialMedia: {
        facebook: "",
        twitter: "",
        linkedin: "",
        instagram: "",
        youtube: "",
        github: "",
      },
      profile: "",
      userStatus: "active",
      emailNotifications: true,
      smsNotifications: true,
      wptoken: "",
      template: "",
      wpCloudApi: "",
      cloudNumber: "",
      phoneNumberId: "",
      wabaId: "",
      businessId: "",
      businessAccountId: "",
      apiKey: "",
      token: "",
      appId: "",
      webhookUrl: "",
      balance: 0,
      expiry: "",
    })
    setSelectedUser(null)
    setCurrentStep(1)
    setSubmitted(false)
    setShowPassword(false)
    setShowConfirmPassword(false)
    setFormError("")
  }

  const openEditModal = (targetUser, initialStep = 1) => {
    setSelectedUser(targetUser)
    setFormData({
      username: targetUser.username || "",
      firstName: targetUser.firstName || "",
      lastName: targetUser.lastName || "",
      email: targetUser.email || "",
      password: "",
      confirmPassword: "",
      role: targetUser.role || "user",
      userStatus: targetUser.userStatus || "active",
      requireEmailVerification: false,
      number: targetUser.number || "",
      operator: targetUser.operator || "cloud",

      // Address fields
      address: targetUser.address || "",
      city: targetUser.city || "",
      state: targetUser.state || "",
      country: targetUser.country || "",
      zipCode: targetUser.zipCode || "",

      // Professional fields
      jobTitle: targetUser.jobTitle || "",
      company: targetUser.company || "",
      department: targetUser.department || "",
      bio: targetUser.bio || "",
      website: targetUser.website || "",

      // Additional contact
      alternateEmail: targetUser.alternateEmail || "",
      alternatePhone: targetUser.alternatePhone || "",

      // Personal info
      dateOfBirth: targetUser.dateOfBirth ? (targetUser.dateOfBirth.includes("T") ? targetUser.dateOfBirth.split("T")[0] : targetUser.dateOfBirth) : "",
      gender: targetUser.gender || "",

      // Social Media - properly initialize nested object
      socialMedia: {
        facebook: targetUser.socialMedia?.facebook || targetUser.facebookUrl || "",
        twitter: targetUser.socialMedia?.twitter || targetUser.twitterUrl || "",
        linkedin: targetUser.socialMedia?.linkedin || targetUser.linkedinUrl || "",
        instagram: targetUser.socialMedia?.instagram || targetUser.instagramUrl || "",
        youtube: targetUser.socialMedia?.youtube || "",
        github: targetUser.socialMedia?.github || "",
      },

      // WhatsApp Business fields
      wptoken: targetUser.wptoken || "",
      template: targetUser.template || "",
      wpCloudApi: targetUser.wpCloudApi || "",
      cloudNumber: targetUser.cloudNumber || "",
      phoneNumberId: targetUser.phoneNumberId || "",
      wabaId: targetUser.wabaId || "",
      businessId: targetUser.businessId || "",
      businessAccountId: targetUser.businessAccountId || "",
      appId: targetUser.appId || "",
      webhookUrl: targetUser.webhookUrl || "",

      // Account settings
      profile: targetUser.profile || "",
      emailNotifications: targetUser.emailNotifications !== undefined ? targetUser.emailNotifications : true,
      smsNotifications: targetUser.smsNotifications !== undefined ? targetUser.smsNotifications : true,
      expiry: targetUser.expiry ? (targetUser.expiry.includes("T") ? targetUser.expiry.split("T")[0] : targetUser.expiry) : "",
      bot: targetUser.bot || false,

      // Legacy WhatsApp fields for backward compatibility
      whatsappNumber: targetUser.whatsappNumber || "",
      whatsappApiKey: targetUser.whatsappApiKey || "",
      whatsappInstanceId: targetUser.whatsappInstanceId || 0,
      balance: targetUser.balance || 0,
    })
    setCurrentStep(initialStep)
    setSubmitted(false)
    setShowPassword(false)
    setShowConfirmPassword(false)
    setFormError("")
    setShowEditModal(true)
  }

  const openCreateModal = () => {
    resetForm()
    setFormData((previous) => ({
      ...previous,
      operator: isAgentManager ? user?.operator || "cloud" : "cloud",
      role: isAgentManager ? "agent" : "user",
    }))
    setShowCreateModal(true)
  }

  const openPricingModal = async (targetUser) => {
    setSelectedUserForPricing(targetUser)
    setShowPricingModal(true)
    setLoadingCountryList(true)

    const cRes = await getUserCountryPricing(targetUser._id || targetUser.id)
    setLoadingCountryList(false)
    if (cRes.success && cRes.countries) {
      setUserCountryList(cRes.countries)
    } else {
      setUserCountryList([])
    }
  }

  const handlePricingSubmit = async (e) => {
    e.preventDefault()

    if (!selectedUserForPricing) return

    setLoading(true)

    try {
      const validPricingData = {}

      // Only include non-empty pricing values
      Object.entries(pricingData).forEach(([key, value]) => {
        if (value && value !== "" && !isNaN(Number.parseFloat(value))) {
          validPricingData[key] = value
        }
      })

      if (Object.keys(validPricingData).length === 0) {
        Swal.fire({
          icon: "warning",
          title: "No Valid Pricing",
          text: "Please enter at least one valid price.",
          confirmButtonColor: "#4A7C59",
        })
        return
      }

      const result = await setUserPricing(selectedUserForPricing._id, validPricingData)

      if (result.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Meta pricing has been set successfully.",
          confirmButtonColor: "#4A7C59",
        })
        setShowPricingModal(false)
        setPricingData({ marketing: "", utility: "", authentication: "" })
        setSelectedUserForPricing(null)
        fetchUsers()
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to set pricing",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (error) {
      console.error("Error in handlePricingSubmit:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to set meta pricing. Please try again.",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const availableRoles = Object.keys(roleHierarchy).filter((role) => canCreateRole(role))

  const renderCreateFormContent = () => {
    return (
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New {managedEntityLabel}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Username *</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className={`w-full px-4 py-3 border ${submitted && !formData.username.trim()
                ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
              placeholder="Enter username"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full px-4 py-3 border ${submitted && !formData.email.trim()
                ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
              placeholder="Enter email address"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password *</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={`w-full pr-12 px-4 py-3 border ${(submitted && !formData.password.trim()) || getPasswordError(formData.password)
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                  } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {getPasswordError(formData.password) ? (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400 font-medium">
                {getPasswordError(formData.password)}
              </p>
            ) : submitted && !formData.password.trim() ? (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400 font-medium">
                Password is required
              </p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Re-enter Password *</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword || ""}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={`w-full pr-12 px-4 py-3 border ${(submitted && (!formData.confirmPassword || !formData.confirmPassword.trim())) || getConfirmPasswordError(formData.confirmPassword, formData.password)
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                  } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                placeholder="Re-enter password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {getConfirmPasswordError(formData.confirmPassword, formData.password) ? (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400 font-medium">
                {getConfirmPasswordError(formData.confirmPassword, formData.password)}
              </p>
            ) : submitted && (!formData.confirmPassword || !formData.confirmPassword.trim()) ? (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400 font-medium">
                Re-enter password is required
              </p>
            ) : null}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number *</label>
            <input
              type="tel"
              value={formData.number}
              onChange={(e) => setFormData({ ...formData, number: e.target.value })}
              className={`w-full px-4 py-3 border ${submitted && !formData.number.trim()
                ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
              placeholder="Enter phone number"
            />
          </div>
          {!isAgentManager && (
            <>
              {["user", "agent"].includes(formData.role) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Operator *</label>
                  <select
                    value={formData.operator}
                    onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="cloud">Cloud</option>
                    <option value="karix">Karix</option>
                    <option value="pinacle">Pinacle</option>
                  </select>
                </div>
              )}
              {!isAgentManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                    required
                  >
                    {availableRoles.map((role) => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>
        {!isAgentManager && (
          <div className="flex flex-col sm:flex-row gap-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="requireEmailVerification"
                checked={formData.requireEmailVerification}
                onChange={(e) => setFormData({ ...formData, requireEmailVerification: e.target.checked })}
                className="w-5 h-5 text-green-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:ring-2"
              />
              <label
                htmlFor="requireEmailVerification"
                className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Require email verification (send verification code)
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="bot"
                checked={formData.bot || false}
                onChange={(e) => setFormData({ ...formData, bot: e.target.checked })}
                className="w-5 h-5 text-green-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:ring-2"
              />
              <label
                htmlFor="bot"
                className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Enable WhatsApp Bot
              </label>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderEditStepContent = () => {
    // Ensure socialMedia object exists to prevent undefined errors
    if (!formData.socialMedia) {
      setFormData({
        ...formData,
        socialMedia: {
          facebook: "",
          twitter: "",
          linkedin: "",
          instagram: "",
          youtube: "",
          github: "",
        },
      })
      return null // Return null to re-render with proper socialMedia object
    }

    if (isAgentManager) {
      return (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Agent Details</h4>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Username *</label>
              <input
                type="text"
                value={formData.username || ""}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className={`w-full px-4 py-3 border ${submitted && (!formData.username || !formData.username.trim())
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                  } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                placeholder="Enter username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email *</label>
              <input
                type="email"
                value={formData.email || ""}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-3 border ${submitted && (!formData.email || !formData.email.trim())
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                  } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password || ""}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`w-full pr-12 px-4 py-3 border ${getPasswordError(formData.password)
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                    } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                  placeholder="Leave blank to keep current password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              {getPasswordError(formData.password) && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400 font-medium">
                  {getPasswordError(formData.password)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number *</label>
              <input
                type="tel"
                value={formData.number || ""}
                onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                className={`w-full px-4 py-3 border ${submitted && (!formData.number || !formData.number.trim())
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                  } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                placeholder="Enter phone number"
              />
            </div>
          </div>
        </div>
      )
    }

    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Step 1: Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Username *</label>
                <input
                  type="text"
                  value={formData.username || ""}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className={`w-full px-4 py-3 border ${submitted && (!formData.username || !formData.username.trim())
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                    } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                  placeholder="Enter username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full px-4 py-3 border ${submitted && (!formData.email || !formData.email.trim())
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                    } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.firstName || ""}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName || ""}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.number || ""}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date of Birth</label>
                <input
                  type="date"
                  value={formData.dateOfBirth || ""}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gender</label>
                <select
                  value={formData.gender || ""}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Alternate Email
                </label>
                <input
                  type="email"
                  value={formData.alternateEmail || ""}
                  onChange={(e) => setFormData({ ...formData, alternateEmail: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter alternate email"
                />
              </div>
            </div>
          </div>
        )
      case 2:
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Step 2: Address Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Address</label>
                <textarea
                  value={formData.address || ""}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  rows="3"
                  placeholder="Enter full address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">City</label>
                <input
                  type="text"
                  value={formData.city || ""}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter city"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">State</label>
                <input
                  type="text"
                  value={formData.state || ""}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter state"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Country</label>
                <input
                  type="text"
                  value={formData.country || ""}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter country"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Zip Code</label>
                <input
                  type="text"
                  value={formData.zipCode || ""}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter zip code"
                />
              </div>
            </div>
          </div>
        )
      case 3:
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Step 3: Professional Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Job Title</label>
                <input
                  type="text"
                  value={formData.jobTitle || ""}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter job title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company</label>
                <input
                  type="text"
                  value={formData.company || ""}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Department</label>
                <input
                  type="text"
                  value={formData.department || ""}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter department"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Website</label>
                <input
                  type="url"
                  value={formData.website || ""}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://example.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bio</label>
                <textarea
                  value={formData.bio || ""}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  rows="4"
                  maxLength="500"
                  placeholder="Tell us about yourself (max 500 characters)"
                />
              </div>
            </div>
          </div>
        )
      case 4:
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Step 4: Social Media Links</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Facebook URL</label>
                <input
                  type="url"
                  value={formData.socialMedia?.facebook || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: { ...formData.socialMedia, facebook: e.target.value },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://facebook.com/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Twitter URL</label>
                <input
                  type="url"
                  value={formData.socialMedia?.twitter || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: { ...formData.socialMedia, twitter: e.target.value },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://twitter.com/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">LinkedIn URL</label>
                <input
                  type="url"
                  value={formData.socialMedia?.linkedin || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: { ...formData.socialMedia, linkedin: e.target.value },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Instagram URL</label>
                <input
                  type="url"
                  value={formData.socialMedia?.instagram || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: { ...formData.socialMedia, instagram: e.target.value },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://instagram.com/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">YouTube URL</label>
                <input
                  type="url"
                  value={formData.socialMedia?.youtube || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: { ...formData.socialMedia, youtube: e.target.value },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://youtube.com/channel/username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">GitHub URL</label>
                <input
                  type="url"
                  value={formData.socialMedia?.github || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMedia: { ...formData.socialMedia, github: e.target.value },
                    })
                  }
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="https://github.com/username"
                />
              </div>
            </div>
          </div>
        )
      case 5:
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Step 5: WhatsApp Business Settings
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  WhatsApp Token
                </label>
                <input
                  type="text"
                  value={formData.wptoken}
                  onChange={(e) => setFormData({ ...formData, wptoken: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter WhatsApp token"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cloud Number</label>
                <input
                  type="text"
                  value={formData.cloudNumber}
                  onChange={(e) => setFormData({ ...formData, cloudNumber: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter cloud number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number ID
                </label>
                <input
                  type="text"
                  value={formData.phoneNumberId}
                  onChange={(e) => setFormData({ ...formData, phoneNumberId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter phone number ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">WABA ID</label>
                <input
                  type="text"
                  value={formData.wabaId}
                  onChange={(e) => setFormData({ ...formData, wabaId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter WABA ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Business ID</label>
                <input
                  type="text"
                  value={formData.businessId}
                  onChange={(e) => setFormData({ ...formData, businessId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter business ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Business Account ID
                </label>
                <input
                  type="text"
                  value={formData.businessAccountId}
                  onChange={(e) => setFormData({ ...formData, businessAccountId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter business account ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">App ID</label>
                <input
                  type="text"
                  value={formData.appId}
                  onChange={(e) => setFormData({ ...formData, appId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter app ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Webhook URL</label>
                <input
                  type="url"
                  value={formData.webhookUrl}
                  onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter webhook URL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Template</label>
                <select
                  value={formData.template}
                  onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select Template</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">WP Cloud API</label>
                <select
                  value={formData.wpCloudApi}
                  onChange={(e) => setFormData({ ...formData, wpCloudApi: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select WP Cloud API</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>            {/* Multiple WABA Numbers Section */}
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Additional WABA Sender Numbers</h4>
              <p className="text-sm text-gray-500 mb-4">Add multiple numbers here to allow this user to select a specific sender number during campaigns.</p>

              <div className="space-y-4">
                {(formData.wabaNumbers || []).map((waba, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 border rounded-xl bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 relative">
                    <button
                      type="button"
                      onClick={() => {
                        const newNumbers = [...(formData.wabaNumbers || [])]
                        newNumbers.splice(index, 1)
                        setFormData({ ...formData, wabaNumbers: newNumbers })
                      }}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 font-bold"
                    >
                      ✕
                    </button>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number ID</label>
                      <input type="text" value={waba.phoneNumberId || ""} onChange={(e) => {
                        const newNumbers = [...formData.wabaNumbers]
                        newNumbers[index].phoneNumberId = e.target.value
                        setFormData({ ...formData, wabaNumbers: newNumbers })
                      }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cloud Number</label>
                      <input type="text" value={waba.cloudNumber || ""} onChange={(e) => {
                        const newNumbers = [...formData.wabaNumbers]
                        newNumbers[index].cloudNumber = e.target.value
                        setFormData({ ...formData, wabaNumbers: newNumbers })
                      }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="e.g. 919999999999" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">WABA ID (Optional)</label>
                      <input type="text" value={waba.wabaId || ""} onChange={(e) => {
                        const newNumbers = [...formData.wabaNumbers]
                        newNumbers[index].wabaId = e.target.value
                        setFormData({ ...formData, wabaNumbers: newNumbers })
                      }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="e.g. 32235..." />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Custom Token (Optional)</label>
                      <input type="text" value={waba.token || ""} onChange={(e) => {
                        const newNumbers = [...formData.wabaNumbers]
                        newNumbers[index].token = e.target.value
                        setFormData({ ...formData, wabaNumbers: newNumbers })
                      }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Label (Optional)</label>
                      <input type="text" value={waba.label || ""} onChange={(e) => {
                        const newNumbers = [...formData.wabaNumbers]
                        newNumbers[index].label = e.target.value
                        setFormData({ ...formData, wabaNumbers: newNumbers })
                      }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="e.g. Sales Team" />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      wabaNumbers: [...(formData.wabaNumbers || []), { phoneNumberId: "", cloudNumber: "", wabaId: "", token: "", label: "" }]
                    })
                  }}
                  className="mt-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 text-sm font-medium rounded-lg transition-colors"
                >
                  + Add Number
                </button>
              </div>
            </div>
          </div>
        )
      case 6:
        return (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Step 6: Account Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password || ""}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`w-full pr-12 px-4 py-3 border ${getPasswordError(formData.password)
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent"
                      } rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200`}
                    placeholder="Leave blank to keep current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {getPasswordError(formData.password) && (
                  <p className="mt-1 text-xs text-red-500 dark:text-red-400 font-medium">
                    {getPasswordError(formData.password)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  required
                >
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User Status</label>
                <select
                  value={formData.userStatus}
                  onChange={(e) => setFormData({ ...formData, userStatus: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              {!isAgentManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Operator</label>
                  <select
                    value={formData.operator}
                    onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="cloud">Cloud</option>
                    <option value="karix">Karix</option>
                    <option value="pinacle">Pinacle</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Balance</label>
                <input
                  type="number"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: Number.parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  min="0"
                  step="0.01"
                  placeholder="Enter balance"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Expiry Date</label>
                <input
                  type="date"
                  value={formData.expiry}
                  onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="emailNotifications"
                      checked={formData.emailNotifications}
                      onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                      className="w-5 h-5 text-green-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                    />
                    <label
                      htmlFor="emailNotifications"
                      className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Email Notifications
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="smsNotifications"
                      checked={formData.smsNotifications}
                      onChange={(e) => setFormData({ ...formData, smsNotifications: e.target.checked })}
                      className="w-5 h-5 text-green-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                    />
                    <label
                      htmlFor="smsNotifications"
                      className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      SMS Notifications
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="bot"
                      checked={formData.bot || false}
                      onChange={(e) => setFormData({ ...formData, bot: e.target.checked })}
                      className="w-5 h-5 text-green-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                    />
                    <label
                      htmlFor="bot"
                      className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      WhatsApp Bot Enabled
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const visibleUsers = getVisibleUsers()
  const currentUserAccount = users.find((u) => u._id === user?._id) || user
  const filteredUsers = visibleUsers.filter((u) => {
    const username = u.username ? u.username.toLowerCase() : ""
    const email = u.email ? u.email.toLowerCase() : ""
    const firstName = u.firstName ? u.firstName.toLowerCase() : ""
    const lastName = u.lastName ? u.lastName.toLowerCase() : ""

    const matchesSearch =
      username.includes(searchTerm.toLowerCase()) ||
      email.includes(searchTerm.toLowerCase()) ||
      firstName.includes(searchTerm.toLowerCase()) ||
      lastName.includes(searchTerm.toLowerCase())

    const matchesRole = isAgentManager || roleFilter === "all" || u.role === roleFilter
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && u.userStatus === "active") ||
      (statusFilter === "inactive" && u.userStatus !== "active")

    return matchesSearch && matchesRole && matchesStatus
  })

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage
  const indexOfFirstUser = indexOfLastUser - usersPerPage
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser)
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage)

  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  return (
    <div className="bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 p-4 lg:p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="border-gray-200 dark:border-gray-700">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent mb-2">
                  {isAgentManager ? "Account & Agent Management" : "User Management"}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  Manage {listEntityLabelPlural} and permissions - {user?.role} view
                </p>
              </div>
              <div className="mt-4 lg:mt-0 flex gap-3">
                {["superadmin", "admin", "reseller", "account", "company", "user"].includes(user?.role) && (
                  <button
                    onClick={navigateToAddBalance}
                    className="bg-gradient-to-r from-green-500 to-yellow-500 hover:from-green-600 hover:to-yellow-500 text-white px-3 py-2 rounded-xl flex items-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                  >
                    <BanknotesIcon className="w-5 h-5" />
                    {["user", "reseller"].includes(user?.role) ? "Transfer Balance" : "Add Balance"}
                  </button>
                )}
                {/* {["superadmin", "admin"].includes(user?.role) && (
                  <button
                    onClick={navigateToMetaPricing}
                    className="bg-gradient-to-r from-green-500 to-yellow-500 hover:from-green-600 hover:to-yellow-500 text-white px-3 py-2 rounded-xl flex items-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                  >
                    <CurrencyDollarIcon className="w-5 h-5" />
                    Set Price
                  </button>
                )} */}
                {canCreateUser() && (
                  <button
                    onClick={openCreateModal}
                    className="bg-gradient-to-r from-green-500 to-yellow-500 hover:from-green-600 hover:to-yellow-500 text-white px-3 py-2 rounded-xl flex items-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add {managedEntityLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="border-gray-200 dark:border-gray-700 mb-8">
          <div className="">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                {/* Search */}
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={isAgentManager ? "Search account or agents..." : "Search users..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-4 py-2 w-full sm:w-80 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  />
                </div>

                {/* Filters */}
                <div className="flex gap-3">
                  {!isAgentManager && (
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 transition-all duration-200"
                    >
                      <option value="all">All Roles</option>
                      <option value="superadmin">Super Admin</option>
                      <option value="admin">Admin</option>
                      <option value="reseller">Reseller</option>
                      <option value="company">Company</option>
                      <option value="user">User</option>
                      <option value="agent">Agent</option>
                    </select>
                  )}

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 transition-all duration-200"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Results count */}
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {currentUsers.length} of {filteredUsers.length} {listEntityLabelPlural}
              </div>
            </div>
          </div>
        </div>

        {/* My Account Details Card (Only for user role / Agent Manager) */}
        {isAgentManager && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8 hover:shadow-md transition-shadow duration-300">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
              <h3 className="text-xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent">
                My Account Details
              </h3>
              <button
                type="button"
                onClick={() => openPricingModal(currentUserAccount || user)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-xl shadow-sm transition-colors"
              >
                <BanknotesIcon className="w-4 h-4" />
                View Meta Pricing Rates
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Account Owner</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">
                    {currentUserAccount?.username || (currentUserAccount?.firstName && currentUserAccount?.lastName ? `${currentUserAccount.firstName} ${currentUserAccount.lastName}` : "N/A")}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center">
                  <EnvelopeIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Email Address</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 truncate max-w-[180px]">
                    {currentUserAccount?.email}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                  <PhoneIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Phone Number</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">
                    {currentUserAccount?.number || "N/A"}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                  <BanknotesIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Current Balance</div>
                  <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                    {formatCurrency(currentUserAccount?.balance || 0)}
                  </div>
                </div>
              </div>
            </div>


          </div>
        )}

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border dark:bg-gray-700/50">
                <tr>
                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">#</th>
                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">{listEntityLabel}</th>
                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">Limit & Sync</th>
                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">Role</th>
                  {["superadmin", "admin"].includes(user?.role) && (
                    <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">Operator</th>
                  )}
                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">Balance</th>

                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">Status</th>
                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">Created</th>
                  <th className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="py-12 text-center text-gray-500 dark:text-gray-400">
                      <UserIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No {listEntityLabelPlural} found</p>
                      <p className="text-sm">Try adjusting your search or filters</p>
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((u, index) => (
                    <tr
                      key={u._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                    >
                      <td className="py-2 px-3">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {indexOfFirstUser + index + 1}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center">
                          <div className="w-11 h-10 bg-gradient-to-br from-green-500 to-yellow-500 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                            <UserIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {u.username || (u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : "N/A")}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{u.email}</div>
                            {u.number && <div className="text-sm text-gray-500 dark:text-gray-400">{u.number}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {!["superadmin", "admin"].includes(u.role) ? (
                          <div className="flex flex-col gap-2">
                            {/* Primary Number Limit */}
                            {(u.cloudNumber || u.phoneNumberId) && (
                              <div className="flex flex-col border-b pb-1 last:border-0 last:pb-0 border-gray-200 dark:border-gray-700">
                                <div className="text-xs text-gray-500 mb-0.5">{u.label || u.cloudNumber || "Primary"}</div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {u.wabaLimit || "Not set"}
                                  </span>
                                  {["superadmin", "admin"].includes(user?.role) && (
                                    <button
                                      onClick={() => handleEditLimit(u, null)}
                                      className="text-blue-500 hover:text-blue-700 p-1 bg-blue-50 dark:bg-blue-900/30 rounded"
                                      title="Edit Limit"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                  {u.operator === "cloud" && ["superadmin", "admin"].includes(user?.role) && (
                                    <button
                                      onClick={() => handleSyncLimit(u, null)}
                                      className="text-green-600 hover:text-green-800 p-1 bg-green-50 dark:bg-green-900/30 rounded"
                                      title="Sync Limit from Meta"
                                    >
                                      <ArrowPathIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                {u.limitUpdatedAt && (
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    Changed: {new Date(u.limitUpdatedAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Additional Numbers Limits */}
                            {u.wabaNumbers?.map((wn, idx) => (
                              <div key={idx} className="flex flex-col border-b pb-1 last:border-0 last:pb-0 border-gray-200 dark:border-gray-700 mt-1">
                                <div className="text-xs text-gray-500 mb-0.5">{wn.label || wn.cloudNumber || `Number ${idx + 1}`}</div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {wn.wabaLimit || "Not set"}
                                  </span>
                                  {["superadmin", "admin"].includes(user?.role) && (
                                    <button
                                      onClick={() => handleEditLimit(u, idx)}
                                      className="text-blue-500 hover:text-blue-700 p-1 bg-blue-50 dark:bg-blue-900/30 rounded"
                                      title="Edit Limit"
                                    >
                                      <PencilIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                  {u.operator === "cloud" && ["superadmin", "admin"].includes(user?.role) && (
                                    <button
                                      onClick={() => handleSyncLimit(u, idx)}
                                      className="text-green-600 hover:text-green-800 p-1 bg-green-50 dark:bg-green-900/30 rounded"
                                      title="Sync Limit from Meta"
                                    >
                                      <ArrowPathIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                {wn.limitUpdatedAt && (
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                                    Changed: {new Date(wn.limitUpdatedAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            ))}
                            {(!u.cloudNumber && !u.phoneNumberId && (!u.wabaNumbers || u.wabaNumbers.length === 0)) && (
                              <div className="text-sm font-medium text-gray-900 dark:text-white">Not set</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${u.role === "superadmin"
                            ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                            : u.role === "admin"
                              ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                              : u.role === "reseller"
                                ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                : u.role === "company"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                  : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            }`}
                        >
                          {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                        </span>
                      </td>
                      {["superadmin", "admin"].includes(user?.role) && (
                        <td className="py-2 px-3 text-center">
                          {(() => {
                            const op = String(u.operator || "cloud").toLowerCase()
                            let code = "C"
                            let badgeStyle = "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/50 dark:text-blue-200"
                            if (op.includes("karix")) {
                              code = "K"
                              badgeStyle = "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/50 dark:text-purple-200"
                            } else if (op.includes("pin")) {
                              code = "P"
                              badgeStyle = "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/50 dark:text-emerald-200"
                            } else if (op.includes("fast")) {
                              code = "F"
                              badgeStyle = "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/50 dark:text-amber-200"
                            }
                            return (
                              <span
                                title={`Operator: ${u.operator || "cloud"}`}
                                className={`w-7 h-7 rounded-full inline-flex items-center justify-center font-extrabold text-xs border shadow-sm ${badgeStyle}`}
                              >
                                {code}
                              </span>
                            )
                          })()}
                        </td>
                      )}
                      <td className="py-2 px-3">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(u.balance)}</span>
                      </td>

                      <td className="py-2 px-3">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            {user.role !== "user" && u._id !== user?._id && (
                              <button
                                onClick={() => toggleUserStatus(u)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${u.userStatus === "active" ? "bg-green-600" : "bg-gray-200"
                                  }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${u.userStatus === "active" ? "translate-x-6" : "translate-x-1"
                                    }`}
                                />
                              </button>
                            )}
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                              {u.userStatus === "active" ? "Active" : "Inactive"}
                            </span>
                          </div>
                          {!u.isEmailVerified && user.role !== "user" && (
                            <button
                              onClick={() => handleSendVerificationCode(u)}
                              className="text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full transition-colors duration-200"
                            >
                              Verify Email
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewUser(u)}
                            className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                            title="View Details"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>

                          {["user", "agent"].includes(u.role) && (
                            <button
                              onClick={() => handleViewWaba(u)}
                              className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                              title="View WABA / WhatsApp Numbers"
                            >
                              <DevicePhoneMobileIcon className="w-4 h-4" />
                            </button>
                          )}

                          {["admin", "superadmin"].includes(user?.role) && u.role === "reseller" && (
                            <button
                              onClick={() => openResellerBrandingModal(u)}
                              className="p-2 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all duration-200"
                              title="Configure Reseller Domain & White-label Branding"
                            >
                              <GlobeAltIcon className="w-4 h-4" />
                            </button>
                          )}

                          {canImpersonateUser(u) && (
                            <button
                              onClick={() => handleImpersonate(u)}
                              className="p-2 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-all duration-200"
                              title="Impersonate"
                            >
                              <UserCircleIcon className="w-4 h-4" />
                            </button>
                          )}

                          {canEditUser(u) && (
                            <button
                              onClick={() => openEditModal(u)}
                              className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                          )}

                          <button
                            onClick={() => (window.location.href = `/userprofile/${u._id}`)}
                            className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
                            title="View Profile"
                          >
                            <ArrowRightIcon className="w-4 h-4" />
                          </button>

                          {/* {canManagePricing() && (
                            <button
                              onClick={() => openPricingModal(u)}
                              className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
                              title="Set Meta Price"
                            >
                              <CurrencyDollarIcon className="w-4 h-4" />
                            </button>
                          )} */}

                          {canDeleteUser(u) && (
                            <button
                              onClick={() => handleDeleteUser(u)}
                              className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredUsers.length)} of{" "}
                  {filteredUsers.length} results
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-all duration-200"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNumber
                    if (totalPages <= 5) {
                      pageNumber = i + 1
                    } else if (currentPage <= 3) {
                      pageNumber = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNumber = totalPages - 4 + i
                    } else {
                      pageNumber = currentPage - 2 + i
                    }

                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`px-3 py-2 text-sm rounded-lg transition-all duration-200 ${currentPage === pageNumber
                          ? "bg-green-600 text-white shadow-lg"
                          : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                          }`}
                      >
                        {pageNumber}
                      </button>
                    )
                  })}

                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-all duration-200"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Create New {managedEntityLabel}</h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false)
                      resetForm()
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateUser} noValidate className="overflow-y-auto max-h-[60vh]">
                <div className="p-3">
                  {formError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 rounded-lg text-red-700 dark:text-red-300 text-sm font-medium">
                      {formError}
                    </div>
                  )}
                  {renderCreateFormContent()}
                </div>

                <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false)
                        resetForm()
                      }}
                      className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-4 py-2 rounded-2xl transition-all duration-200 flex items-center gap-2 font-semibold"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          {formData.requireEmailVerification ? "Send Verification" : `Create ${managedEntityLabel}`}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {showUserDetails && selectedUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-end p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-l-2xl w-full max-w-md h-full overflow-y-auto shadow-2xl">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{managedEntityLabel} Details</h3>
                <button
                  onClick={() => {
                    setShowUserDetails(false)
                    setSelectedUser(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Name</div>
                  <div className="text-lg font-medium text-gray-900 dark:text-white">{selectedUser.displayName}</div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Email</div>
                  <div className="text-lg text-gray-900 dark:text-white">{selectedUser.email}</div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Role</div>
                    <div className="text-gray-900 dark:text-white">{selectedUser.role}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Status</div>
                    <div className="text-gray-900 dark:text-white">{selectedUser.status}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Phone</div>
                    <div className="text-gray-900 dark:text-white">{selectedUser.phone}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Balance</div>
                    <div className="text-gray-900 dark:text-white">{selectedUser.balance}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Email Verified</div>
                    <div className="text-gray-900 dark:text-white">{selectedUser.emailVerified ? "Yes" : "No"}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Created</div>
                    <div className="text-gray-900 dark:text-white">{selectedUser.createdAt}</div>
                  </div>
                </div>

                {selectedUser.raw && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Numbers Onboarding</div>
                        <div className="text-gray-900 dark:text-white">
                          {((selectedUser.raw.cloudNumber || selectedUser.raw.phoneNumberId || selectedUser.raw.wabaId || selectedUser.raw.token || selectedUser.raw.wptoken) ? 1 : 0) + (selectedUser.raw.wabaNumbers?.length || 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">WhatsApp Bot Enabled</div>
                        <div className="text-gray-900 dark:text-white">{selectedUser.raw.bot ? "Yes" : "No"}</div>
                      </div>
                    </div>

                    {/* Display primary and secondary numbers */}
                    {(((selectedUser.raw.cloudNumber || selectedUser.raw.phoneNumberId || selectedUser.raw.wabaId || selectedUser.raw.token || selectedUser.raw.wptoken) ? 1 : 0) + (selectedUser.raw.wabaNumbers?.length || 0) > 0) && (
                      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
                        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">WhatsApp Numbers</div>
                        <div className="space-y-2">
                          {(selectedUser.raw.cloudNumber || selectedUser.raw.phoneNumberId || selectedUser.raw.wabaId || selectedUser.raw.token || selectedUser.raw.wptoken) ? (
                            <div className="text-sm flex flex-col">
                              <span className="text-xs text-gray-500 uppercase tracking-wider">Primary Number</span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {selectedUser.raw.label ? `${selectedUser.raw.label} - ` : ""}
                                {selectedUser.raw.cloudNumber || selectedUser.raw.number || "No number configured"}
                              </span>
                            </div>
                          ) : null}
                          {selectedUser.raw.wabaNumbers?.map((wn, idx) => (
                            <div key={idx} className="text-sm flex flex-col">
                              <span className="text-xs text-gray-500 uppercase tracking-wider">Additional Number {idx + 1}</span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {wn.label ? `${wn.label} - ` : ""}
                                {wn.cloudNumber || wn.number || "No number configured"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Additional fields (social, company etc.) */}
                {selectedUser.raw && (
                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    {selectedUser.raw.company && (
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Company</div>
                        <div className="text-gray-900 dark:text-white">{selectedUser.raw.company}</div>
                      </div>
                    )}
                    {selectedUser.raw.socialMedia && (
                      <div className="mt-3">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Social</div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {Object.entries(selectedUser.raw.socialMedia).map(([k, v]) =>
                            v ? (
                              <a key={k} href={v} target="_blank" rel="noreferrer" className="text-sm text-green-600 dark:text-green-400">
                                {k}
                              </a>
                            ) : null
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showVerificationModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full shadow-2xl">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Email Verification</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Enter the verification code sent to {verificationEmail}
                </p>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter 6-digit code"
                      maxLength="6"
                    />
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowVerificationModal(false)
                      setVerificationCode("")
                      setVerificationEmail("")
                    }}
                    className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVerifyEmail}
                    disabled={loading || !verificationCode}
                    className="bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 font-semibold"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        Verify Email
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal with Steps */}
        {showEditModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      Edit {managedEntityLabel}: {selectedUser.username || selectedUser.email}
                    </h3>
                    {!isAgentManager && (
                      <div className="flex items-center mt-2 space-x-2">
                        {([1, 2, 3, 4, 5, 6].filter(s => !(!["user", "agent"].includes(formData.role) && s === 5))).map((step, index) => (
                          <div
                            key={step}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === currentStep
                              ? "bg-green-600 text-white"
                              : step < currentStep
                                ? "bg-green-200 text-green-800"
                                : "bg-gray-200 text-gray-600"
                              }`}
                          >
                            {index + 1}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false)
                      resetForm()
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleUpdateUser} noValidate className="overflow-y-auto max-h-[60vh]">
                <div className="p-6">
                  {formError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500 rounded-lg text-red-700 dark:text-red-300 text-sm font-medium">
                      {formError}
                    </div>
                  )}
                  {renderEditStepContent()}
                </div>

                <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex justify-between">
                    {!isAgentManager ? (
                      <button
                        type="button"
                        onClick={prevStep}
                        disabled={currentStep === 1}
                        className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                    ) : (
                      <div />
                    )}

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditModal(false)
                          resetForm()
                        }}
                        className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-all duration-200"
                      >
                        Cancel
                      </button>

                      {isAgentManager || currentStep === 6 ? (
                        <button
                          type="submit"
                          disabled={loading}
                          className="bg-green-700 hover:bg-green-800 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 font-semibold"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Updating...
                            </>
                          ) : (
                            <>
                              <CheckIcon className="w-4 h-4" />
                              Update Agent
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={nextStep}
                          className="bg-green-700 hover:bg-green-800 text-white px-6 py-2 rounded-lg transition-all duration-200 font-semibold"
                        >
                          Next
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
        {showPricingModal && selectedUserForPricing && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Country Rates & Permissions</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      User: <span className="font-semibold text-gray-700 dark:text-gray-300">{selectedUserForPricing.name || selectedUserForPricing.username || selectedUserForPricing.email}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowPricingModal(false)
                      setSelectedUserForPricing(null)
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto space-y-6">
                {loadingCountryList ? (
                  <div className="py-12 flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  </div>
                ) : (
                  <>
                    {/* 1. ENABLED COUNTRIES */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                        Enabled Countries
                      </h4>
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-green-50 dark:bg-gray-700 text-green-900 dark:text-green-300">
                            <tr>
                              <th className="py-2.5 px-3">Country</th>
                              <th className="py-2.5 px-3">Code</th>
                              {/* <th className="py-2.5 px-3">Marketing</th>
                              <th className="py-2.5 px-3">Utility</th>
                              <th className="py-2.5 px-3">Authentication</th> */}
                              <th className="py-2.5 px-3" style={{width:"10%"}}>Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {userCountryList
                              .filter((c) => c.isAllowed || c.countryCode === "91")
                              .map((c) => (
                                <tr key={c.id || c.countryCode} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="py-2.5 px-3 font-semibold text-gray-800 dark:text-gray-200">{c.countryName}</td>
                                  <td className="py-2.5 px-3 font-mono text-blue-600 dark:text-blue-400">+{c.countryCode}</td>
                                  {/* <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-white">₹{c.rates?.marketingDisplay ?? c.rates?.marketing ?? "1.0"}</td>
                                  <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-white">₹{c.rates?.utilityDisplay ?? c.rates?.utility ?? "0.15"}</td>
                                  <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-white">₹{c.rates?.authenticationDisplay ?? c.rates?.authentication ?? "0.15"}</td> */}
                                  <td className="py-2.5 px-3">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                      Enabled
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* 2. OTHER COUNTRIES (NOT ENABLED) */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-gray-400"></span>
                        Other Countries
                      </h4>
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            <tr>
                              <th className="py-2.5 px-3">Country</th>
                              <th className="py-2.5 px-3">Code</th>
                              <th className="py-2.5 px-3">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {userCountryList
                              .filter((c) => c.countryCode !== "91" && !c.isAllowed)
                              .map((c) => (
                                <tr key={c.id || c.countryCode} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="py-2.5 px-3 font-medium text-gray-700 dark:text-gray-300">{c.countryName}</td>
                                  <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400">+{c.countryCode}</td>
                                  <td className="py-2.5 px-3">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                      Not Enabled
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowPricingModal(false)
                    setSelectedUserForPricing(null)
                  }}
                  className="px-5 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showWabaModal && selectedWabaUser && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                    <PhoneIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">WhatsApp & WABA Sender Numbers</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      User: {selectedWabaUser.firstName || selectedWabaUser.username || selectedWabaUser.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowWabaModal(false)
                    setSelectedWabaUser(null)
                    handleCancelWabaEdit()
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                  Primary WhatsApp Configuration
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block">Operator / Provider</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {selectedWabaUser.operator || "cloud"}  </span>

                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block">WabaId</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {selectedWabaUser.wabaId}  </span>

                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {wabaModalForm.editType ? (wabaModalForm.editType === "primary" ? "Edit Primary Number" : `Edit Additional Sender #${wabaModalForm.editIndex + 1}`) : "Add New Sender Number"}
                  </h4>
                  {wabaModalForm.editType && (
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full font-medium">
                      Editing Mode
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Label</span>
                    <input
                      type="text"
                      value={wabaModalForm.label}
                      onChange={(e) => setWabaModalForm({ ...wabaModalForm, label: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200"
                      placeholder="Enter Label"
                    />
                  </div>

                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Phone Number ID</span>
                    <input
                      type="number"
                      value={wabaModalForm.phoneNumberId}
                      onChange={(e) => setWabaModalForm({ ...wabaModalForm, phoneNumberId: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200"
                      placeholder="Enter PhonenumberId"
                    />
                  </div>

                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Cloud Number</span>
                    <input
                      type="number"
                      value={wabaModalForm.cloudNumber}
                      onChange={(e) => setWabaModalForm({ ...wabaModalForm, cloudNumber: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200"
                      placeholder="Enter Cloud Number"
                    />
                  </div>

                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">WabaId</span>
                    <input
                      type="text"
                      value={wabaModalForm.wabaId || ""}
                      onChange={(e) => setWabaModalForm({ ...wabaModalForm, wabaId: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200"
                      placeholder="Enter WabaId"
                    />
                  </div>

                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Token</span>
                    <input
                      type="text"
                      value={wabaModalForm.token}
                      onChange={(e) => setWabaModalForm({ ...wabaModalForm, token: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 focus:ring-green-500 focus:border-transparent rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 transition-all duration-200"
                      placeholder="Enter token"
                    />
                  </div>
                </div>

                <div className="flex justify-start gap-2 pt-4">
                  <button onClick={handleCancelWabaEdit} type="button" className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-gray-800 dark:text-white text-xs font-medium rounded-lg">Cancel</button>
                  <button onClick={handleSaveDirectWabaEdit} disabled={loading} type="button" className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg shadow-sm">{loading ? "Saving..." : "Save Changes"}</button>
                </div>
              </div>

              <div className="p-6 space-y-6 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Label / Username</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Phone Number ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Cloud Number</th>

                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Token</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800 text-sm">
                    {/* Primary Number Row */}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {selectedWabaUser.label || selectedWabaUser.username || selectedWabaUser.email || "Primary"}
                          </span>
                          <span className="px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs rounded-full font-medium">
                            Primary
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">
                        {selectedWabaUser.phoneNumberId || "-"}
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">
                        {selectedWabaUser.cloudNumber || selectedWabaUser.number || "-"}
                      </td>

                      <td className="px-4 py-3 font-mono text-emerald-600 dark:text-emerald-400">
                        {selectedWabaUser.token || selectedWabaUser.apiKey || selectedWabaUser.wptoken ? maskToken(selectedWabaUser.token || selectedWabaUser.apiKey || selectedWabaUser.wptoken) : "-"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex items-left gap-3">
                          {canEditUser(selectedWabaUser) && (
                            <button
                              onClick={() => handleStartDirectWabaEdit("primary", 0, selectedWabaUser)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                              title="Edit Number"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Additional WABA Numbers Rows */}
                    {(selectedWabaUser.wabaNumbers || []).map((waba, idx) => (
                      <tr key={idx} className="group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {waba.label || `Sender #${idx + 1}`}
                            </span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 text-xs rounded-full font-medium">
                              Additional
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">
                          {waba.phoneNumberId || "-"}
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-700 dark:text-gray-300">
                          {waba.cloudNumber || "-"}
                        </td>
                        <td className="px-4 py-3 font-mono text-gray-600 dark:text-gray-400">
                          {waba.token ? (
                            <span className="text-emerald-600 dark:text-emerald-400">{maskToken(waba.token)}</span>
                          ) : (
                            <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">Uses Primary Token</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-left gap-3">
                            {canEditUser(selectedWabaUser) && (
                              <button
                                onClick={() => handleStartDirectWabaEdit("additional", idx, waba)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                title="Edit Number"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                            )}
                            {canEditUser(selectedWabaUser) && (
                              <button
                                onClick={() => handleSetPrimaryWaba(idx)}
                                className="text-amber-500 hover:text-amber-600 transition-colors"
                                title="Set as Primary Sender"
                              >
                                <StarIcon className="w-5 h-5" />
                              </button>
                            )}
                            {canEditUser(selectedWabaUser) && (
                              <button
                                onClick={() => handleDeleteWabaNumber("additional", idx, waba)}
                                className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 transition-all duration-200"
                                title="Delete Number"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl">

                <button
                  onClick={() => {
                    setShowWabaModal(false)
                    setSelectedWabaUser(null)
                    handleCancelWabaEdit()
                  }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reseller White-label Branding Modal */}
        {showResellerBrandingModal && selectedResellerForBranding && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-xl w-full shadow-2xl overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white dark:from-gray-800 dark:to-gray-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
                    <GlobeAltIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Reseller Domain & White-label Branding
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Reseller: {selectedResellerForBranding.username || selectedResellerForBranding.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowResellerBrandingModal(false)
                    setSelectedResellerForBranding(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSaveResellerBranding} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Custom Reseller Domain
                  </label>
                  <input
                    type="text"
                    value={resellerBrandingForm.domain}
                    onChange={(e) =>
                      setResellerBrandingForm({ ...resellerBrandingForm, domain: e.target.value })
                    }
                    placeholder="e.g. whatsapp.yourdomain.com or localhost"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Matched when users open the app from this domain.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Custom Reseller Server IP
                  </label>
                  <input
                    type="text"
                    value={resellerBrandingForm.ip}
                    onChange={(e) =>
                      setResellerBrandingForm({ ...resellerBrandingForm, ip: e.target.value })
                    }
                    placeholder="e.g. 103.45.12.99"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Matched if domain is not configured or for direct IP access.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Company / Brand Name
                  </label>
                  <input
                    type="text"
                    value={resellerBrandingForm.companyName}
                    onChange={(e) =>
                      setResellerBrandingForm({ ...resellerBrandingForm, companyName: e.target.value })
                    }
                    placeholder="e.g. DigiForest Solutions"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Reseller Logo Image
                  </label>
                  <div className="space-y-3">
                    <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-xl flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 min-h-[56px]">
                      <span className="text-xs text-gray-500 font-medium">Logo Preview:</span>
                      {resellerBrandingForm.logo ? (
                        <img
                          src={
                            resellerBrandingForm.logo.startsWith("http")
                              ? resellerBrandingForm.logo
                              : `${API_BASE_URL.replace(/\/api$/, "")}${resellerBrandingForm.logo}`
                          }
                          alt="Reseller Logo Preview"
                          className="h-10 max-w-[160px] object-contain"
                        />
                      ) : (
                        <span className="text-xs italic text-gray-400">Blank (No logo uploaded)</span>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors inline-block shadow-sm">
                        Upload Logo Image
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleLogoUploadInModal(file)
                          }}
                        />
                      </label>

                      {resellerBrandingForm.logo ? (
                        <button
                          type="button"
                          onClick={() =>
                            setResellerBrandingForm({ ...resellerBrandingForm, logo: "" })
                          }
                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 font-semibold"
                        >
                          Remove Logo (Make Blank)
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResellerBrandingModal(false)
                      setSelectedResellerForBranding(null)
                    }}
                    className="px-5 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={brandingLoading}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
                  >
                    {brandingLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        Save Branding Details
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Useraccount
